import React from "react";

import { formatCalibrationRate } from "./calibrationCheckAverages";

import "./FieldAppHarvestLossSpreadsheet.css";
import "./FieldAppPlantingCalibrationOrderResults.css";

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
}

function formatSeedSupply(row, t) {
  if (row.seedQuantity == null) return "—";
  const unit = row.seedUnit || t("un.");
  return `${Number(row.seedQuantity).toLocaleString("pt-BR", {
    maximumFractionDigits: 2,
  })} ${unit}`;
}

export function FieldAppPlantingCalibrationAverages({
  summary,
  variant = "planting",
  t,
}) {
  if (!summary || !summary.count) return null;
  const isFertilization = variant === "fertilization";

  return (
    <div className="field-app-harvest-loss-spreadsheet__section">
      <h4>{t("Médias em destaque")}</h4>
      <table className="table table-striped jambo_table field-app-harvest-loss-spreadsheet__table">
        <thead>
          <tr>
            <th>{t("Métrica")}</th>
            <th>{t("Área")}</th>
            <th>{t("ha/h")}</th>
            <th>{isFertilization ? t("Fertilizante") : t("Adubo")}</th>
            {isFertilization ? null : <th>{t("Semente")}</th>}
            {isFertilization ? null : <th>{t("Inoculante")}</th>}
          </tr>
        </thead>
        <tbody>
          <SummaryRows summary={summary} variant={variant} t={t} />
        </tbody>
      </table>
    </div>
  );
}

function SummaryRows({ summary, variant = "planting", t }) {
  if (!summary || !summary.count) return null;
  const isFertilization = variant === "fertilization";

  const last = summary.last || {};
  const last3Label =
    summary.last3Count > 1
      ? t("Média últimas {{count}}", { count: summary.last3Count })
      : null;

  const extraCells = (row) =>
    isFertilization ? null : (
      <>
        <td>
          {formatCalibrationRate(row.seedAppliedThousandPerHa, t("mil/ha"))}
        </td>
        <td>{formatCalibrationRate(row.inoculantAppliedLHa, t("L/ha"))}</td>
      </>
    );

  return (
    <>
      <tr>
        <td>{t("Última abastecida")}</td>
        <td>{formatCalibrationRate(last.hectaresPlanted, t("ha"), 2)}</td>
        <td>{formatCalibrationRate(last.hectaresPerHour, t("ha/h"), 2)}</td>
        <td>{formatCalibrationRate(last.fertilizerAppliedKgHa, t("kg/ha"))}</td>
        {extraCells(last)}
      </tr>
      {last3Label ? (
        <tr>
          <td>{last3Label}</td>
          <td>
            {formatCalibrationRate(summary.last3.hectaresPlanted, t("ha"), 2)}
          </td>
          <td>
            {formatCalibrationRate(summary.last3.hectaresPerHour, t("ha/h"), 2)}
          </td>
          <td>
            {formatCalibrationRate(
              summary.last3.fertilizerAppliedKgHa,
              t("kg/ha")
            )}
          </td>
          {extraCells(summary.last3)}
        </tr>
      ) : null}
      {summary.count > 1 ? (
        <tr>
          <td>{t("Média / total do período")}</td>
          <td>
            {formatCalibrationRate(summary.total.hectaresPlanted, t("ha"), 2)}
          </td>
          <td>
            {formatCalibrationRate(summary.total.hectaresPerHour, t("ha/h"), 2)}
          </td>
          <td>
            {formatCalibrationRate(
              summary.total.fertilizerAppliedKgHa,
              t("kg/ha")
            )}
          </td>
          {extraCells(summary.total)}
        </tr>
      ) : null}
    </>
  );
}

function LaunchRow({ row, index, variant = "planting", t }) {
  return (
    <tr
      className={
        row.outsideTolerance
          ? "field-app-planting-calibration-row--alert"
          : undefined
      }
    >
      <td>{index + 1}</td>
      <td>{formatTimestamp(row.timestamp)}</td>
      <td>{row.operatorName || "—"}</td>
      <td>{row.machineName || "—"}</td>
      <td>{formatCalibrationRate(row.panelHectaresInitial, "", 1)}</td>
      <td>{formatCalibrationRate(row.panelHectares, "", 1)}</td>
      <td>{formatCalibrationRate(row.hectaresPlanted, t("ha"), 2)}</td>
      <td>{formatCalibrationRate(row.fertilizerKg, t("kg"))}</td>
      <td>{formatCalibrationRate(row.fertilizerAppliedKgHa, t("kg/ha"))}</td>
      <td>{formatCalibrationRate(row.fertilizerTargetKgHa, t("kg/ha"))}</td>
      {variant === "fertilization" ? null : (
        <>
          <td>{formatSeedSupply(row, t)}</td>
          <td>
            {formatCalibrationRate(row.seedAppliedThousandPerHa, t("mil/ha"))}
          </td>
          <td>{formatCalibrationRate(row.seedTarget, t("mil/ha"))}</td>
          <td>
            {formatCalibrationRate(row.seedAppliedPerMeter, t("sem/m"), 2)}
          </td>
          <td>{formatCalibrationRate(row.inoculantL, t("L"))}</td>
          <td>{formatCalibrationRate(row.inoculantAppliedLHa, t("L/ha"))}</td>
        </>
      )}
      <td>
        {row.outsideTolerance ? t("Fora da tolerância") : t("Dentro")}
      </td>
    </tr>
  );
}

export default function FieldAppPlantingCalibrationSpreadsheet({
  rows,
  summary,
  orderLabel,
  variant = "planting",
  t,
}) {
  const isFertilization = variant === "fertilization";
  return (
    <div className="field-app-harvest-loss-spreadsheet field-app-planting-calibration-spreadsheet">
      <div className="field-app-harvest-loss-spreadsheet__print-header">
        <h3 className="field-app-harvest-loss-spreadsheet__title">
          {t(
            isFertilization
              ? "Planilha de fertilização — check de calibragem"
              : "Planilha de plantio — check de calibragem"
          )}
        </h3>
        {orderLabel ? (
          <p className="field-app-harvest-loss-spreadsheet__meta">{orderLabel}</p>
        ) : null}
      </div>

      <FieldAppPlantingCalibrationAverages
        summary={summary}
        variant={variant}
        t={t}
      />

      {rows && rows.length ? (
        <div className="field-app-harvest-loss-spreadsheet__section">
          <h4>{t("Lançamentos do app")}</h4>
          <div className="field-app-planting-calibration-spreadsheet__scroll">
            <table className="table table-striped jambo_table field-app-harvest-loss-spreadsheet__table field-app-harvest-loss-spreadsheet__table--samples">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("Data/Hora")}</th>
                  <th>{t("Operador")}</th>
                  <th>{t("Máquina")}</th>
                  <th>{t("Painel início")}</th>
                  <th>{t("Painel")}</th>
                  <th>
                    {isFertilization ? t("ha fertilizados") : t("ha plantados")}
                  </th>
                  <th>{t("Adubo (kg)")}</th>
                  <th>{t("Adubo kg/ha")}</th>
                  <th>{t("Alvo adubo")}</th>
                  {isFertilization ? null : (
                    <>
                      <th>{t("Semente abastecida")}</th>
                      <th>{t("Semente mil/ha")}</th>
                      <th>{t("Alvo semente")}</th>
                      <th>{t("Sementes/m")}</th>
                      <th>{t("Inoculante (L)")}</th>
                      <th>{t("Inoculante L/ha")}</th>
                    </>
                  )}
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <LaunchRow
                    key={row.uuid || `${row.timestamp}-${index}`}
                    row={row}
                    index={index}
                    variant={variant}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
